import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import {
  lensPath,
  lensProp,
  pick,
  propEq,
  set,
  equals,
  find,
  mergeRight,
} from "ramda";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import {
  validateProfile,
  validateProfileField,
} from "../organizationValidation";
import Button from "@material-ui/core/Button";
import {
  useOrganizationProfileUpdate,
  useUsersWithRole,
} from "../organizationServices";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import LibraryTypeSelect from "./LibraryTypeSelect";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useErrorBox } from "./use-errorBox";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const EditOrganizationProfileDialog = ({
  open,
  organizationId,
  profile,
  onCancel,
  onSave,
}) => {
  const classes = useStyles();

  const [fields, setFields] = useState({});
  useEffect(() => {
    if (open && notBlank(profile)) {
      setFields(
        pick(
          [
            "id",
            "libraryTypeId",
            "salesPersonAccountId",
            "salesPerson",
            "oauthDisplayName",
            "offlineBookshelfMax",
            "marketingUrlName",
            "marketingUrl",
            "libraryUrl",
            "libraryCardUrl",
            "excludeFromReports",
            "showSelfe",
            "showBuyLink",
            "selfeEnabled",
            "showShareLinks",
            "bannerAdMobileApp",
            "attributionStmt",
          ],
          profile,
        ),
      );
    } else {
      setFields({
        id: organizationId,
      });
    }
  }, [open, organizationId, profile]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const [salesUsers, setSalesUsers] = useState([]);
  const usersResponse = useUsersWithRole("ROLE_SALES_PERSON");
  const [selectedSalesUser, setSelectedSalesUser] = useState(null);
  useEffect(() => {
    if (usersResponse.isSuccess && notBlank(usersResponse.data)) {
      setSalesUsers(usersResponse.data);
      if (notBlank(fields.salesPersonAccountId)) {
        const user = find(
          propEq("userId", fields.salesPersonAccountId),
          usersResponse.data,
        );
        console.log(user);

        setSelectedSalesUser(user);
      }
    }
  }, [
    usersResponse.isSuccess,
    usersResponse.data,
    fields.salesPersonAccountId,
  ]);

  const handleBlur = (field, value) => {
    const errors = validateProfileField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleSalesPersonChange = useCallback((_event, user) => {
    setFields(prev => set(lensProp("salesPersonAccountId"), user.userId, prev));
  }, []);

  const updateProfile = useOrganizationProfileUpdate();
  const handleApply = useCallback(async () => {
    const validationErrors = validateProfile(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      try {
        const previous = isBlank(profile) ? {} : profile;
        const update = mergeRight(previous, fields);
        await updateProfile.mutateAsync({
          resourceIds: { id: update.id },
          data: update,
        });
        onSave(fields);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [
    fields,
    profile,
    updateProfile,
    onSave,
    setGeneralError,
    setValidationErrors,
  ]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle id="edit-organization-profile-dialog-title">
        Edit Organization Profile
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item sm={6}>
            <LibraryTypeSelect
              value={fields.libraryTypeId}
              onChange={handleTextChange(["libraryTypeId"])}
            />
          </Grid>

          <Grid item sm={6}>
            <Autocomplete
              id="sales-person"
              options={salesUsers}
              getOptionLabel={user => notBlank(user) && `${user.username}`}
              getOptionSelected={(opt, val) =>
                notBlank(val) && equals(opt.userId, val.userId)
              }
              value={selectedSalesUser}
              onChange={handleSalesPersonChange}
              renderInput={params => (
                <TextField {...params} label="Sales Person" />
              )}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="oauth-display-name"
              fullWidth
              label="OAuth Display Name"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.oauthDisplayName}
              onChange={handleTextChange(["oauthDisplayName"])}
              error={hasValidationError("oauthDisplayName")}
              helperText={firstValidationError("oauthDisplayName")}
              onBlur={() =>
                handleBlur("oauthDisplayName", fields.oauthDisplayName)
              }
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="offline-bookshelf-max"
              fullWidth
              label="Offline Bookshelf Max"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.offlineBookshelfMax}
              onChange={handleTextChange(["offlineBookshelfMax"])}
              error={hasValidationError("offlineBookshelfMax")}
              helperText={firstValidationError("offlineBookshelfMax")}
              onBlur={() =>
                handleBlur("offlineBookshelfMax", fields.offlineBookshelfMax)
              }
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="marketing-url-name"
              fullWidth
              label="Marketing URL Name"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.marketingUrlName}
              onChange={handleTextChange(["marketingUrlName"])}
              error={hasValidationError("marketingUrlName")}
              helperText={firstValidationError("marketingUrlName")}
              onBlur={() =>
                handleBlur("marketingUrlName", fields.marketingUrlName)
              }
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="marketing-url"
              fullWidth
              label="Marketing URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.marketingUrl}
              onChange={handleTextChange(["marketingUrl"])}
              error={hasValidationError("marketingUrl")}
              helperText={firstValidationError("marketingUrl")}
              onBlur={() => handleBlur("marketingUrl", fields.marketingUrl)}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="library-url"
              fullWidth
              label="Library URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.libraryUrl}
              onChange={handleTextChange(["libraryUrl"])}
              error={hasValidationError("libraryUrl")}
              helperText={firstValidationError("libraryUrl")}
              onBlur={() => handleBlur("libraryUrl", fields.libraryUrl)}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="library-card-url"
              fullWidth
              label="Library Card URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.libraryCardUrl}
              onChange={handleTextChange(["libraryCardUrl"])}
              error={hasValidationError("libraryCardUrl")}
              helperText={firstValidationError("libraryCardUrl")}
              onBlur={() => handleBlur("libraryCardUrl", fields.libraryCardUrl)}
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="attribution-statement"
              fullWidth
              multiline
              label="Attribution Statement"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.attributionStmt}
              onChange={handleTextChange(["attributionStmt"])}
              error={hasValidationError("attributionStmt")}
              helperText={firstValidationError("attributionStmt")}
              onBlur={() =>
                handleBlur("attributionStmt", fields.attributionStmt)
              }
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.excludeFromReports}
                  onChange={handleCheckboxChange("excludeFromReports")}
                  name="exclude-from-reports"
                  inputProps={{ "aria-label": "exclude from reports" }}
                />
              }
              label="Exclude From Reports"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.showSelfe}
                  onChange={handleCheckboxChange("showSelfe")}
                  name="show-selfe"
                  inputProps={{ "aria-label": "show i a p" }}
                />
              }
              label="Show IAP"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.selfeEnabled}
                  onChange={handleCheckboxChange("selfeEnabled")}
                  name="selfe-enabled"
                  inputProps={{ "aria-label": "i a p enabled" }}
                />
              }
              label="IAP Enabled"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.showBuyLink}
                  onChange={handleCheckboxChange("showBuyLink")}
                  name="show-buy-link"
                  inputProps={{ "aria-label": "show buy link" }}
                />
              }
              label="Show Buy Link"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.showShareLinks}
                  onChange={handleCheckboxChange("showShareLinks")}
                  name="show-share-link"
                  inputProps={{ "aria-label": "show share link" }}
                />
              }
              label="Show Share Links"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.bannerAdMobileApp}
                  onChange={handleCheckboxChange("bannerAdMobileApp")}
                  name="banner-ad-mobile-app"
                  inputProps={{ "aria-label": "banner add mobile app" }}
                />
              }
              label="Banner Ad Mobile App"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrganizationProfileDialog;
